<template>
  <div>
    <popup-add-stock-product
      v-if="product"
      ref="popupAddProduct"
      :alreadySelectedProducts="product.stockIngredients"
      :selectableStockProducts="stockProducts"
      @onSelected="onSelectedNewStockProduct"
    />

    <popup-add-sales-product
      v-if="product"
      ref="popupAddSalesProduct"
      :alreadySelectedProducts="product.splitSalesProductReferences"
      :selectableSalesProducts="salesProducts"
      @onSelected="onSelectedNewSalesProduct"
    />

    <popup-add-package
      v-if="product"
      ref="popupAddPackage"
      :alreadySelectedPackages="product.choicePackages"
      :selectablePackages="choicePackages"
      @onSelected="onSelectedNewPackage"
    />

    <RightSidebar :isActive.sync="isActive" size="large">
      <template #header>
        <img width="40" :src="ProductIcon" class="mr-4" />
        <div>
          <h4>{{ isNewProduct ? "New " : "" }}Sales product</h4>
          <small v-if="product"> {{ product.id }} • {{ product.name }} </small>
        </div>
      </template>

      <template #content>
        <Loading v-if="isLoading" />
        <div v-else>
          <!-- <section class="px-6 pt-6 grid grid-cols-4 gap-x-8">
            <SidebarItem label="Country">
              <vs-select
                v-model="product.countryId"
                class="w-full"
                :disabled="!isNewProduct"
              >
                <vs-select-item
                  :key="index"
                  :value="item.countryId.id"
                  :text="item.name"
                  v-for="(item, index) in countries"
                />
              </vs-select>
            </SidebarItem>
          </section> -->

          <!-- <vs-divider /> -->

          <section class="px-6 grid grid-cols-4 gap-x-8 mt-4">
            <SidebarItem label="Product name" class="col-span-2">
              <div class="w-full">
                <vs-input class="w-full" v-model="product.name" />
                <!-- <vs-checkbox v-model="product.mustSetName" class="mt-2" label="Default">Set name on sale</vs-checkbox> -->
                <vs-checkbox v-model="product.staffDrinkEligible" class="mt-2" label="Default">Staff drink eligible</vs-checkbox>
              </div>
            </SidebarItem>

            <SidebarItem label="Color" class="col-span-2 row-span-2">
              <input
                type="color"
                v-model="product.color"
                class="w-32 h-32 rounded-lg border border-solid custom-color-picker"
                style="border-color: #eee"
                :style="'--color-picker-color:' + product.color + '62'"
              />
            </SidebarItem>

            <SidebarItem label="Guide text (optional)" class="col-span-2 row-span-2">
              <vs-textarea class="w-full h-full" v-model="product.helpText" />
            </SidebarItem>
            <SidebarItem label="Double sales product" v-if="!isPriceable">
              <vs-select v-model="product.doubleProductId" autocomplete class="w-full">
                <vs-select-item key="empty" :value="null" text="None" />
                <vs-select-item :key="item.id" :value="item.id" :text="item.name" v-for="item in salesProducts" />
              </vs-select>
            </SidebarItem>
            <SidebarItem label="Triple sales product" v-if="!isPriceable">
              <vs-select v-model="product.tripleProductId" autocomplete class="w-full">
                <vs-select-item key="empty" :value="null" text="None" />
                <vs-select-item :key="item.id" :value="item.id" :text="item.name" v-for="item in salesProducts" />
              </vs-select>
            </SidebarItem>

            <SidebarItem label="Sales group" class="col-start-1">
              <vs-select v-model="product.salesProductGroupId" class="w-full">
                <vs-select-item :key="index" :value="item.salesProductGroupId.id" :text="item.name" v-for="(item, index) in salesGroups" />
              </vs-select>
            </SidebarItem>

            <SidebarItem label="Sub sales group">
              <vs-select v-model="product.salesProductSubGroupId" class="w-full">
                <vs-select-item key="empty" :value="null" text="None" />
                <vs-select-item
                  :key="index"
                  :value="item.salesProductSubGroupId.id"
                  :text="item.name"
                  v-for="(item, index) in salesSubGroups"
                />
              </vs-select>
            </SidebarItem>

            <SidebarItem label="Product type">
              <vs-select v-model="product.salesProductType" :disabled="!isNewProduct">
                <vs-select-item
                  :key="item.value"
                  :value="item.value"
                  :text="item.name"
                  v-for="item in [
                    { value: 'Stock', name: 'Stock type' },
                    { value: 'Split', name: 'Split type' },
                    { value: 'Priceable', name: 'Priceable type' }
                  ]"
                />
              </vs-select>
            </SidebarItem>

            <SidebarItem label="VAT rate" v-if="!isSplit">
              <vs-select v-model="product.taxGroupId">
                <vs-select-item :key="index" :value="item.taxGroupId.id" :text="item.code" v-for="(item, index) in taxGroups" />
              </vs-select>
            </SidebarItem>
          </section>
          <section class="grid grid-cols-5 gap-4 px-6" v-if="isSplit">
            <SidebarItem v-for="totalPrice in splitTotalPrices" :key="totalPrice.priceGroupId.id" :label="totalPrice.name + ' (min.)'">
              <NumberInput unit="DKK" :value="totalPrice.price" :disabled="true" />
            </SidebarItem>
          </section>
          <section class="grid grid-cols-5 gap-4 px-6" v-else-if="!isPriceable">
            <SidebarItem v-for="priceGroup in priceGroups" :key="priceGroup.priceGroupId.id" :label="priceGroup.name + ' (min.)'">
              <NumberInput
                unit="DKK"
                :min="0"
                :value="matchingPriceGroupPrice(product.minPrices, priceGroup.priceGroupId.id)"
                @input="updatePrice(product.minPrices, priceGroup.priceGroupId.id, $event)"
              />
            </SidebarItem>
          </section>

          <!-- <vs-divider class="col-span-full mb-2" /> -->

          <h3 class="ml-4 mt-4 mb-2" v-if="!isPriceable">Ingredients</h3>
          <div class="table-primary-bg compact-table mx-6 data-list-list-view" v-if="isSplit">
            <vs-table :data="product.splitSalesProductReferences">
              <template slot="thead">
                <vs-th sort-key="number">Product</vs-th>
                <vs-th v-for="priceGroup in priceGroups" :key="priceGroup.priceGroupId.id">{{ priceGroup.name }}</vs-th>
              </template>

              <template slot-scope="{ data }">
                <tbody>
                  <vs-tr :data="tr" :key="tr.id" v-for="tr in data" class="cursor-default">
                    <vs-td>
                      <div class="product-name font-medium truncate flex flex-row items-center">
                        <!-- <img width="40" :src="ProductIcon" /> -->
                        <div class="flex flex-col">
                          <div class="whitespace-nowrap overflow-hidden truncate" style="width: 206px">
                            {{ tr.salesProduct.name }}
                          </div>
                          <div class="text-sm text-grey">
                            {{ tr.salesProductId }}
                          </div>
                        </div>
                      </div>
                    </vs-td>
                    <vs-td v-for="priceGroup in priceGroups" :key="priceGroup.priceGroupId.id">
                      <NumberInput
                        :min="0"
                        :value="matchingPriceGroupPrice(tr.minPrices, priceGroup.priceGroupId.id)"
                        @input="updatePrice(tr.minPrices, priceGroup.priceGroupId.id, $event)"
                        style="width: 78px"
                      />
                    </vs-td>
                    <vs-td class="whitespace-no-wrap">
                      <feather-icon
                        icon="TrashIcon"
                        class="cursor-pointer"
                        svgClasses="w-5 h-5 hover:text-danger stroke-current"
                        @click.stop="removeSalesProduct(tr)"
                      />
                    </vs-td>
                  </vs-tr>
                </tbody>
              </template>
            </vs-table>

            <div class="flex justify-end mt-2 mr-1">
              <vs-button type="border" class="ml-2 py-2 px-3" @click="$refs.popupAddSalesProduct.show()">
                <feather-icon icon="PlusIcon" svgClasses="w-4 h-4 pt-1" />
                <span class="ml-1">Add sales product</span>
              </vs-button>
              <!-- <vs-button
              type="border"
              class="ml-4"
              @click="$refs.popupAddCategory.show()"
            >
              <feather-icon icon="PlusIcon" svgClasses="w-4 h-4" />
              <span class="ml-1">Add category</span>
            </vs-button> -->
            </div>
          </div>

          <div class="table-primary-bg compact-table mx-6 data-list-list-view" v-else-if="!isPriceable">
            <vs-table :data="product.stockIngredients">
              <template slot="thead">
                <vs-th sort-key="number">Product</vs-th>
                <vs-th sort-key="amount">Amount</vs-th>
                <vs-th></vs-th>
                <vs-th></vs-th>
                <vs-th sort-key="canBeReplaced">Can be replaced?</vs-th>
                <vs-th></vs-th>
              </template>

              <template slot-scope="{ data }">
                <tbody>
                  <vs-tr :data="tr" :key="tr.id" v-for="tr in data" class="cursor-default">
                    <vs-td>
                      <div class="product-name font-medium truncate flex flex-row items-center">
                        <!-- <img width="40" :src="ProductIcon" /> -->
                        <div class="flex flex-col">
                          <div class="whitespace-nowrap overflow-hidden truncate" style="width: 206px">
                            {{ tr.stockProduct.name }}
                          </div>
                          <div class="flex items-center">
                            <!-- <div class="mr-2">
                              <vx-tooltip
                                :text="
                                  isFollowingADefaultPrice(tr)
                                    ? 'Following a default price of the stock product. If the default price of the stock product changes, this price will be updated accordingly.'
                                    : 'NOT following a default price of the stock product. If the default price of the stock product changes, this price will NOT be updated accordingly.'
                                "
                                position="top"
                              >
                                <div
                                  class="w-4 h-4 rounded-full transition-all duration-300 ease-in-out"
                                  :class="{
                                    'bg-success': isFollowingADefaultPrice(tr),
                                    'bg-grey opacity-50':
                                      !isFollowingADefaultPrice(tr),
                                  }"
                                ></div>
                              </vx-tooltip>
                            </div> -->

                            <div class="text-sm text-grey">
                              {{ tr.stockProduct.number }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </vs-td>
                    <vs-td>
                      <NumberSelectInput
                        v-model="tr.amount"
                        :unit.sync="tr.productUnit"
                        :units="units"
                        style="width: 142px"
                        :compact="true"
                      />

                      <div v-if="tr.canBeReplaced" class="mt-3">
                        <div class="mb-0">
                          <b>House</b>
                        </div>
                        <NumberInput
                          class="mt-1"
                          v-model="tr.replaceMinPriceCategory1"
                          :min="0"
                          unit="+ DKK"
                          style="width: 124px"
                          :compact="true"
                        />
                      </div>
                    </vs-td>
                    <vs-td>
                      <!-- <NumberInput
                        v-model="tr.priceMin"
                        unit="DKK"
                        style="width: 124px"
                        :compact="true"
                      /> -->

                      <div v-if="tr.canBeReplaced" style="margin-top: 44px">
                        <div class="mb-0">
                          <b>Lux</b>
                        </div>
                        <NumberInput
                          class="mt-1"
                          v-model="tr.replaceMinPriceCategory2"
                          :min="0"
                          unit="+ DKK"
                          style="width: 124px"
                          :compact="true"
                        />
                      </div>
                    </vs-td>
                    <vs-td>
                      <!-- <div class="h-8 flex items-center">
                        {{
                          calculateCogs(
                            tr.productUnit,
                            tr.amount,
                            tr.priceMin,
                            tr.stockProduct.unitPrice,
                            tr.stockProduct.productUnitInLiters
                          )
                        }}
                        %
                      </div> -->

                      <div v-if="tr.canBeReplaced" style="margin-top: 44px">
                        <div class="mb-0">
                          <b>Premium</b>
                        </div>
                        <NumberInput
                          class="mt-1"
                          v-model="tr.replaceMinPriceCategory3"
                          :min="0"
                          unit="+ DKK"
                          style="width: 124px"
                          :compact="true"
                        />
                      </div>
                    </vs-td>
                    <vs-td class="flex flex-col">
                      <vs-select v-model="tr.canBeReplaced" style="width: 68px">
                        <vs-select-item
                          :key="item.value"
                          :value="item.value"
                          :text="item.name"
                          v-for="item in [
                            { value: false, name: 'No' },
                            { value: true, name: 'Yes' }
                          ]"
                        />
                      </vs-select>
                      <div v-if="tr.canBeReplaced" class="mt-8">
                        <div>
                          <small class="whitespace-no-wrap absolute mt-5"> Min. additional price </small>
                        </div>
                      </div>
                    </vs-td>
                    <vs-td class="whitespace-no-wrap">
                      <feather-icon
                        icon="TrashIcon"
                        class="cursor-pointer"
                        svgClasses="w-5 h-5 hover:text-danger stroke-current"
                        @click.stop="removeItem(tr)"
                      />
                    </vs-td>
                  </vs-tr>
                </tbody>
              </template>
            </vs-table>

            <div class="flex justify-end mt-2 mr-1">
              <vs-button type="border" class="ml-2 py-2 px-3" @click="$refs.popupAddProduct.show()">
                <feather-icon icon="PlusIcon" svgClasses="w-4 h-4 pt-1" />
                <span class="ml-1">Add stock product</span>
              </vs-button>
            </div>
          </div>

          <div v-if="!isSplit && !isPriceable">
            <h3 class="mt-5 ml-4 mb-2">Package</h3>
            <p class="ml-4 text-sm max-w-md">
              A package is a collection of stock products where one item of the package can be selected when ordering this sales product.
            </p>
            <div class="flex items-start pb-2 mt-2 w-full">
              <div
                class="table-primary-bg mx-6 data-list-list-view"
                style="min-width: 520px"
                v-for="(choicePackage, index) in product.choicePackages"
                :key="index"
              >
                <vs-table :data="choicePackage.stockIngredients">
                  <template slot="header">
                    <div class="flex justify-between items-center w-full">
                      <h5 class="ml-0 mb-2">{{ choicePackage.name }}</h5>

                      <vs-button type="border" color="danger" size="small" @click="onClickRemovePackage(choicePackage.id)">
                        <span class="ml-1">Remove package</span>
                      </vs-button>
                    </div>
                  </template>
                  <template slot="thead">
                    <vs-th sort-key="number">Product</vs-th>
                    <vs-th sort-key="amount">Amount</vs-th>
                  </template>

                  <template slot-scope="{ data }">
                    <tbody>
                      <vs-tr :data="tr" :key="tr.stockProductId" v-for="tr in data" class="cursor-default">
                        <vs-td>
                          <div class="product-name font-medium truncate flex flex-row items-center">
                            <!-- <img width="40" :src="ProductIcon" /> -->
                            <div class="flex flex-col">
                              <div class="whitespace-nowrap overflow-hidden truncate" style="width: 206px">
                                {{ tr.stockProduct.name }}
                              </div>
                              <div class="text-sm text-grey">
                                {{ tr.stockProduct.number }}
                              </div>
                            </div>
                          </div>
                        </vs-td>
                        <vs-td>
                          <NumberSelectInput
                            v-model="tr.amount"
                            :unit.sync="tr.productUnit"
                            :units="units"
                            style="width: 142px"
                            :compact="true"
                            :disabled="true"
                          />
                        </vs-td>
                      </vs-tr>
                    </tbody>
                  </template>
                </vs-table>
              </div>
              <div class="mx-4 mt-2">
                <vs-button
                  radius
                  type="filled"
                  icon="add"
                  v-if="product.choicePackages.length < 1 && !isSplit"
                  @click="$refs.popupAddPackage.show()"
                >
                </vs-button>
              </div>
            </div>
          </div>
        </div>

        <!-- Spacer -->
        <div class="h-24"></div>
      </template>

      <template #footer>
        <!-- <section class="grid grid-cols-5 gap-x-8 mb-2" v-if="product">
          <SidebarItem label="Total cost">
            <NumberInput
              v-model="pricingMetrics.cost"
              unit="DKK"
              :disabled="true"
            />
          </SidebarItem>

          <SidebarItem label="Total price">
            <NumberInput
              v-model="pricingMetrics.price"
              unit="DKK"
              :disabled="true"
            />
          </SidebarItem>

          <SidebarItem label="Total profit">
            <NumberInput
              v-model="pricingMetrics.profit"
              unit="DKK"
              :disabled="true"
            />
          </SidebarItem>

          <SidebarItem label="Profit margin">
            <NumberInput
              v-model="pricingMetrics.profitMargin"
              unit="%"
              :disabled="true"
            />
          </SidebarItem>

          <SidebarItem label="Cogs">
            <NumberInput
              v-model="pricingMetrics.cogs"
              unit="%"
              :disabled="true"
            />
          </SidebarItem>
        </section> -->

        <vs-button @click="submitData" :disabled="!isFormValid">{{ isNewProduct ? "Create" : "Save" }}</vs-button>
        <vs-button type="border" color="danger" class="ml-6" @click="isActive = false">Cancel</vs-button>
        <vs-button v-if="!isNewProduct" type="border" class="ml-auto" @click="assignToTestVenue">Assign to LGD venue</vs-button>
      </template>
    </RightSidebar>
  </div>
</template>

<script>
import ProductIcon from "@/assets/images/product-icon.png";
import moment from "moment";
import { mapGetters } from "vuex";
import VenueSelector from "@/components/VenueSelector.vue";
import RightSidebar from "@/components/Custom/RightSidebar.vue";
import SidebarItem from "@/components/Custom/SidebarItem.vue";
import NumberSelectInput from "@/components/Custom/NumberSelectInput.vue";
import NumberInput from "@/components/Custom/NumberInput.vue";
import PopupAddStockProduct from "./PopupAddStockProduct.vue";
import PopupAddSalesProduct from "./PopupAddSalesProduct.vue";
import PopupAddPackage from "./PopupAddPackage.vue";
import { calculateCogs } from "@/utils/posUtils.js";
import _ from "lodash";

export default {
  components: {
    VenueSelector,
    RightSidebar,
    SidebarItem,
    NumberSelectInput,
    NumberInput,
    PopupAddStockProduct,
    PopupAddSalesProduct,
    PopupAddPackage
  },

  data() {
    return {
      ProductIcon,
      calculateCogs,

      isActive: false,
      isLoading: true,

      isNewProduct: false,
      product: null
    };
  },
  computed: {
    ...mapGetters({
      activeVenue: "venues/activeVenue",
      activeCountry: "venues/activeCountry",
      countries: "pos/countries/countries",
      taxGroups: "pos/countrySettings/taxGroups",
      priceGroups: "pos/countrySettings/priceGroups",
      salesProducts: "pos/salesProducts/salesProducts",
      stockProducts: "pos/stockProducts/stockProducts",
      choicePackages: "pos/packages/packages",
      salesGroups: "pos/countrySettings/salesProductGroups",
      units: "pos/countrySettings/units"
    }),
    isPriceable() {
      return this.product.salesProductType === "Priceable";
    },
    isSplit() {
      return this.product.salesProductType === "Split";
    },
    salesSubGroups() {
      const salesGroup = this.salesGroups.find(g => g.salesProductGroupId.id === this.product.salesProductGroupId);

      if (salesGroup) return salesGroup.validSubGroups;
      return [];
    },
    isFormValid() {
      return true; // TODO
    },

    splitTotalPrices() {
      return this.priceGroups.map(e => {
        return {
          priceGroupId: e.priceGroupId,
          name: e.name,
          price: _.sumBy(this.product.splitSalesProductReferences, i => {
            const match = i.minPrices.find(p => p.priceGroupId === e.priceGroupId.id);
            return match ? parseFloat(match.price) : 0;
          })
        };
      });
    },

    pricingMetrics() {
      const unitMatrix = {
        ml: 0.001,
        cl: 0.01,
        dl: 0.1,
        l: 1
      };

      const measureableUnitsOfMeasure = ["pcs", "btl", "can", "unit", "glass", "bag"]; // TODO: Get from API

      // Total cost, total price, total profit, profit margin, cogs
      // Use lodash
      const cost = _.sumBy(this.product.stockIngredients, i => {
        if (measureableUnitsOfMeasure.includes(i.productUnit)) {
          return i.stockProduct.unitPrice * i.amount;
        } else {
          const unitInLiters = unitMatrix[i.productUnit];
          return (i.stockProduct.unitPrice / i.stockProduct.productUnitInLiters) * i.amount * unitInLiters;
        }
      }).toFixed(2);
      const price = _.sumBy(this.product.stockIngredients, i => i.priceMin).toFixed(2);
      const profit = (price - cost).toFixed(2);
      const profitMargin = ((profit / price) * 100).toFixed(2);
      const cogs = ((cost / price) * 100).toFixed(2);

      return {
        cost,
        price,
        profit,
        profitMargin,
        cogs
      };
    }
  },
  methods: {
    async show(salesProductId) {
      this.isNewProduct = false;
      this.isActive = true;
      this.isLoading = true;

      this.product = await this.$store.dispatch("pos/salesProducts/getSalesProduct", salesProductId);
      console.log("Product:", this.product);
      await this.loadExternalData();

      this.isLoading = false;
    },
    async showEmptyProduct() {
      this.isNewProduct = true;

      this.product = {
        countryId: "dk", // TODO
        name: "",
        description: "",
        color: "#ffbabe",
        mustSetName: false,
        mustSetPrice: false,
        staffDrinkEligible: false,
        helpText: "",
        minPrices: [],
        taxGroupId: null,
        salesProductType: "Stock",
        salesProductGroupId: null,
        salesProductSubGroupId: null,
        doubleProductId: null,
        tripleProductId: null,
        stockIngredients: [],
        splitSalesProductReferences: [],
        choicePackages: [],

        createdAction: null
      };

      this.isActive = true;
      this.isLoading = true;
      await this.loadExternalData();
      this.isLoading = false;
    },

    async loadExternalData() {
      await Promise.all([
        // this.$store.dispatch("pos/countries/getCountries"),
        this.$store.dispatch("pos/countrySettings/getCountrySettings"),
        this.$store.dispatch("pos/stockProducts/getStockProducts"),
        this.$store.dispatch("pos/packages/getPackages")
      ]);
    },

    matchingPriceGroupPrice(priceGroupPrices, priceGroupId) {
      const match = priceGroupPrices.find(p => p.priceGroupId === priceGroupId);
      return match ? match.price : "";
    },

    updatePrice(priceGroupPrices, priceGroupId, price) {
      console.log("Updating price", priceGroupPrices, priceGroupId, price);
      const match = priceGroupPrices.find(p => p.priceGroupId === priceGroupId);
      if (match) {
        match.price = price;
      } else {
        priceGroupPrices.push({
          priceGroupId,
          price
        });
      }
    },

    formatDate(date) {
      return moment(date).format("DD-MM-YYYY HH:mm");
    },

    async createNew(product) {
      const dto = {
        ...product,
        choicePackageIds: product.choicePackages.map(p => p.id)
      };
      return await this.$store.dispatch("pos/salesProducts/createSalesProduct", dto);
    },

    async updateExisting(product) {
      const dto = {
        ...product,
        choicePackageIds: product.choicePackages.map(p => p.id)
      };

      await this.$store.dispatch("pos/salesProducts/updateSalesProduct", {
        salesProductId: product.id,
        salesProductDto: dto
      });
    },

    async submitData() {
      console.log("Submitting DTO", this.product);
      let newCreatedProductResponse = null;

      try {
        if (this.isNewProduct) {
          newCreatedProductResponse = await this.createNew(this.product);
        } else {
          await this.updateExisting(this.product);
        }

        this.product = null;
        this.isActive = false;
        this.isLoading = true;

        console.log("New created product", newCreatedProductResponse);

        // If we just created a new product. Show the sidebar again with the product
        if (newCreatedProductResponse) {
          this.show(newCreatedProductResponse);
        }

        this.$store.dispatch("pos/salesProducts/getSalesProducts");

        this.$vs.notify({
          title: "Success",
          text: `Successfully ${this.isNewProduct ? "created new" : "updated"} sales product`
        });
      } catch (error) {
        this.$vs.notify({
          title: "Error",
          color: "danger",
          text: `Failed to ${this.isNewProduct ? "create" : "update"} sales product: ${error}`
        });

        this.isLoading = false;
      }
    },

    onSelectedNewStockProduct(stockProduct) {
      console.log("Selected new stock product", stockProduct);

      const stockProductSalesItemDto = {
        amount: 1,
        canBeReplaced: false,
        replaceMinPriceCategory1: 0,
        replaceMinPriceCategory2: 0,
        replaceMinPriceCategory3: 0,

        stockProductId: stockProduct.id,
        productUnit: stockProduct.productUnit,
        stockProduct
      };

      // // Take the first default pricing of the stockProduct, if it exists
      // if (stockProduct.defaultPricings.length > 0) {
      //   const defaultPricing = stockProduct.defaultPricings[0];
      //   stockProductSalesItemDto.amount = defaultPricing.amount;
      //   stockProductSalesItemDto.productUnit = defaultPricing.productUnit;
      // }

      this.product.stockIngredients.push(stockProductSalesItemDto);
    },

    onSelectedNewSalesProduct(salesProduct) {
      console.log("Selected new sales product", salesProduct);

      const splitSalesProductReferenceViewModel = {
        minPrices: [],
        salesProductId: salesProduct.id,
        salesProduct: salesProduct
      };

      this.product.splitSalesProductReferences.push(splitSalesProductReferenceViewModel);
    },

    onSelectedNewPackage(choicePackage) {
      this.product.choicePackages.push(choicePackage);
    },

    onClickRemovePackage(packageId) {
      console.log("Want to remove package", packageId);
      this.product.choicePackages = this.product.choicePackages.filter(choicePackage => choicePackage.id !== packageId);
    },

    removeItem(item) {
      console.log("Want to remove item", item);
      this.product.stockIngredients = this.product.stockIngredients.filter(i => i.stockProductId !== item.stockProductId);
    },

    removeSalesProduct(item) {
      console.log("Want to remove sales product", item);
      this.product.splitSalesProductReferences = this.product.splitSalesProductReferences.filter(
        i => i.salesProductId !== item.salesProductId
      );
    },

    async assignToTestVenue() {
      console.log("Want to assign to test venue");
      try {
        await this.$store.dispatch("pos/assignedSalesProducts/assignSalesProduct", {
          venueId: "cfae18d5-a9d7-451b-aa9e-d253456ff64d",
          salesProductId: this.product.id
        });

        this.$vs.notify({
          title: "Success",
          text: `Successfully assigned sales product to LGD venue`
        });
      } catch (error) {
        this.$vs.notify({
          title: "Error",
          color: "danger",
          text: `Failed to assign sales product to LGD venue: ${error}`
        });
      }
    }
  }
};
</script>

<style scoped lang="scss">
.custom-color-picker {
  appearance: none;
  border: none;
  background-color: transparent;

  &::-webkit-color-swatch {
    border: none;
    border-radius: 12px;
    box-shadow: 2px 4px 12px 0 var(--color-picker-color);
  }
  &::-webkit-color-swatch-wrapper {
    padding: 16px;
  }
}
</style>